import type { CodeableConcept, Coverage, CoverageClass, Patient, Reference } from '@medplum/fhirtypes';
import { generateId, getExtensionValue, resolveId, type PatchOperation } from '@medplum/core';
import { EXTENSIONS } from './constants';
import { IDENTIFIER_SYSTEMS } from './identifiers';

/**
 * HL7 Coverage Class codes — used on `Coverage.class.type`.
 * See https://terminology.hl7.org/CodeSystem-coverage-class.html.
 */
export const COVERAGE_CLASS_SYSTEM = 'http://terminology.hl7.org/CodeSystem/coverage-class';

/** `group` — Employer / plan group number in {@link COVERAGE_CLASS_SYSTEM}. */
export const COVERAGE_CLASS_GROUP = 'group';

/** `plan` — Plan name or identifier in {@link COVERAGE_CLASS_SYSTEM} (e.g. MassHealth Limited). */
export const COVERAGE_CLASS_PLAN = 'plan';

/** Max insurance policies a patient can have on create / add UI. */
export const MAX_INSURANCE_COVERAGES = 3;

/**
 * Insurance fields used to display and edit a FHIR `Coverage`.
 * `insurer` is `Coverage.payor.display` and is required on write. When the insurer was picked from
 * the shared payer catalog (`Organization` `type=ins`), `payerOrganizationId` is set and writes use
 * `{ reference, display }`; free-text insurers leave the id null and write display-only.
 * `insuredName` is `subscriber.display`. `memberId` is `subscriberId`.
 * `planName` is `Coverage.class` with type `plan`. `groupId` is `Coverage.class` with type `group`.
 *
 * Care-team creates stamp {@link IDENTIFIER_SYSTEMS.COVERAGE_SOURCE} for provenance; imported
 * Coverages do not carry it. Insurance lists filter by `ownership: 'internal'` (beneficiary is the
 * Liza Patient) rather than this identifier, so Metriport/HIE policies on shadow children stay out
 * of the editor.
 */
export type InsuranceFields = {
  /** Required on write. Null only when projecting an incomplete imported Coverage. */
  insurer: string | null;
  /**
   * Medplum `Organization` id of a catalog payer when the insurer was selected from typeahead.
   * Null for free-text insurers.
   */
  payerOrganizationId: string | null;
  /** Person the policy is in the name of. May differ from the patient. */
  insuredName: string | null;
  /** Member / subscriber ID on the insurance card (`Coverage.subscriberId`). */
  memberId: string | null;
  /** Plan name on the card (`Coverage.class` type `plan`), e.g. MassHealth Limited. */
  planName: string | null;
  /** Employer / plan group number (`Coverage.class` type `group`). */
  groupId: string | null;
};

/**
 * Write shape for {@link InsuranceFields}. `insurer` is required (non-blank after trim); other
 * fields are optional. Blank strings are treated as absent.
 */
export type InsuranceInput = {
  insurer?: string | null;
  payerOrganizationId?: string | null;
  insuredName?: string | null;
  memberId?: string | null;
  planName?: string | null;
  groupId?: string | null;
};

const blankToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

/** Provenance stamp for a Liza-authored Coverage. Value is a unique id per policy. */
const newCoverageSourceIdentifier = (): { system: string; value: string } => ({
  system: IDENTIFIER_SYSTEMS.COVERAGE_SOURCE,
  value: generateId(),
});

const isCoverageClassCode = (entry: CoverageClass, code: string): boolean =>
  entry.type?.coding?.some((coding) => {
    const entryCode = coding.code?.trim();
    if (entryCode !== code) return false;
    return !coding.system || coding.system === COVERAGE_CLASS_SYSTEM;
  }) ?? false;

const isManagedCoverageClass = (entry: CoverageClass): boolean =>
  isCoverageClassCode(entry, COVERAGE_CLASS_GROUP) || isCoverageClassCode(entry, COVERAGE_CLASS_PLAN);

const coverageClassConcept = (code: string, display: string): CodeableConcept => ({
  coding: [
    {
      system: COVERAGE_CLASS_SYSTEM,
      code,
      display,
    },
  ],
  text: display,
});

const coverageClassValue = (classes: Coverage['class'] | undefined, code: string): string | null => {
  const entry = classes?.find((item) => isCoverageClassCode(item, code));
  return blankToNull(entry?.value);
};

/**
 * Plan name from `Coverage.class` type `plan`. Prefers FHIR `class.name` (human-readable in the
 * HL7 examples) and falls back to `class.value` (where we also write free-text plan names).
 */
const planNameFromCoverageClass = (classes: Coverage['class'] | undefined): string | null => {
  const entry = classes?.find((item) => isCoverageClassCode(item, COVERAGE_CLASS_PLAN));
  return blankToNull(entry?.name) ?? blankToNull(entry?.value);
};

/** Read the group number from `Coverage.class`, or null when absent. */
const groupIdFromCoverageClass = (classes: Coverage['class'] | undefined): string | null =>
  coverageClassValue(classes, COVERAGE_CLASS_GROUP);

/**
 * Merge plan name + group id into `Coverage.class`. Preserves unmanaged class entries (rxbin, etc.).
 * `null` clears that managed entry. Plan writes both `value` and `name` so round-trips match the
 * HL7 Coverage examples (human-readable plan text on `name`).
 */
const applyManagedCoverageClasses = (
  existing: Coverage['class'] | undefined,
  values: { planName: string | null; groupId: string | null },
): Coverage['class'] | undefined => {
  const others = (existing ?? []).filter((entry) => !isManagedCoverageClass(entry));
  const next = [...others];
  if (values.planName) {
    next.push({
      type: coverageClassConcept(COVERAGE_CLASS_PLAN, 'Plan'),
      value: values.planName,
      name: values.planName,
    });
  }
  if (values.groupId) {
    next.push({ type: coverageClassConcept(COVERAGE_CLASS_GROUP, 'Group'), value: values.groupId });
  }
  return next.length > 0 ? next : undefined;
};

/**
 * Normalize a write into a FHIR-ready {@link InsuranceFields}, or `null` when insurer is absent.
 * A payer org id without an insurer display is dropped with the insurer.
 */
export const normalizeInsuranceInput = (
  input: InsuranceInput | null | undefined,
): (InsuranceFields & { insurer: string }) | null => {
  if (!input) return null;
  const insurer = blankToNull(input.insurer);
  if (!insurer) return null;
  return {
    insurer,
    payerOrganizationId: blankToNull(input.payerOrganizationId),
    insuredName: blankToNull(input.insuredName),
    memberId: blankToNull(input.memberId),
    planName: blankToNull(input.planName),
    groupId: blankToNull(input.groupId),
  };
};

const payerForFields = (
  fields: Pick<InsuranceFields, 'payerOrganizationId'> & { insurer: string },
): NonNullable<Coverage['payor']> => {
  if (fields.payerOrganizationId) {
    return [
      {
        reference: `Organization/${fields.payerOrganizationId}`,
        display: fields.insurer,
      },
    ];
  }
  return [{ display: fields.insurer }];
};

/**
 * Build a new `Coverage` from insurance form fields. Does not set `meta.accounts` — the server
 * inherits the practice compartment from `beneficiary`. Does not set `Coverage.order`
 * (primary/secondary is not collected yet). Stamps {@link IDENTIFIER_SYSTEMS.COVERAGE_SOURCE} for
 * provenance. Insurance lists use `ownership: 'internal'`, not this identifier.
 */
export const buildCoverage = (medplumPatientId: string, fields: InsuranceFields & { insurer: string }): Coverage => {
  const insuredName = fields.insuredName;
  const memberId = fields.memberId;
  const classEntries = applyManagedCoverageClasses(undefined, {
    planName: fields.planName,
    groupId: fields.groupId,
  });
  return {
    resourceType: 'Coverage',
    status: 'active',
    identifier: [newCoverageSourceIdentifier()],
    beneficiary: { reference: `Patient/${medplumPatientId}` },
    payor: payerForFields(fields),
    ...(insuredName ? { subscriber: { display: insuredName } } : {}),
    ...(memberId ? { subscriberId: memberId } : {}),
    ...(classEntries ? { class: classEntries } : {}),
  };
};

/**
 * Apply insurance fields onto an existing Coverage, preserving id/meta/identifiers and any existing
 * `Coverage.order` (we do not collect primary/secondary yet).
 */
export const applyInsuranceFieldsToCoverage = (
  existing: Coverage,
  fields: InsuranceFields & { insurer: string },
): Coverage => {
  const insuredName = fields.insuredName;
  const memberId = fields.memberId;
  const updated: Coverage = {
    ...existing,
    status: existing.status ?? 'active',
    payor: payerForFields(fields),
  };
  if (insuredName) {
    updated.subscriber = { ...(existing.subscriber ?? {}), display: insuredName };
  } else {
    delete updated.subscriber;
  }
  if (memberId) {
    updated.subscriberId = memberId;
  } else {
    delete updated.subscriberId;
  }
  const nextClass = applyManagedCoverageClasses(existing.class, {
    planName: fields.planName,
    groupId: fields.groupId,
  });
  if (nextClass) {
    updated.class = nextClass;
  } else {
    delete updated.class;
  }
  return updated;
};

const payerOrganizationIdFromReference = (reference: Reference | undefined): string | null => {
  if (!reference) return null;
  const id = resolveId(reference);
  return id || null;
};

/** Display/edit fields from any `Coverage` (imported or care-team). */
export const insuranceFieldsFromCoverage = (
  coverage: Pick<Coverage, 'payor' | 'subscriber' | 'subscriberId' | 'class'> | null | undefined,
): InsuranceFields => {
  const primaryPayer =
    coverage?.payor?.find((entry) => entry.display?.trim() || entry.reference) ?? coverage?.payor?.[0];
  return {
    insurer: blankToNull(primaryPayer?.display),
    payerOrganizationId: payerOrganizationIdFromReference(primaryPayer),
    insuredName: blankToNull(coverage?.subscriber?.display),
    memberId: blankToNull(coverage?.subscriberId),
    planName: planNameFromCoverageClass(coverage?.class),
    groupId: groupIdFromCoverageClass(coverage?.class),
  };
};

/** True when the patient explicitly answered that they have no insurance. */
export const hasNoInsurance = (patient: Pick<Patient, 'extension'> | null | undefined): boolean =>
  getExtensionValue(patient ?? {}, EXTENSIONS.NO_INSURANCE) === true;

/**
 * Set or clear {@link EXTENSIONS.NO_INSURANCE} on a Patient. `true` stamps the extension; `false`
 * removes it (unanswered / now insured). An empty extension list is omitted rather than `[]`.
 */
export const withNoInsuranceExtension = (patient: Patient, noInsurance: boolean): Patient => {
  const without = (patient.extension ?? []).filter((ext) => ext.url !== EXTENSIONS.NO_INSURANCE);
  if (!noInsurance) {
    if (without.length === 0) {
      const next = { ...patient };
      delete next.extension;
      return next;
    }
    return { ...patient, extension: without };
  }
  return { ...patient, extension: [...without, { url: EXTENSIONS.NO_INSURANCE, valueBoolean: true }] };
};

/**
 * JSON Patch ops to set or clear {@link EXTENSIONS.NO_INSURANCE}. Rewrites only `/extension` so
 * concurrent writes to other Patient fields are not clobbered.
 */
export const buildNoInsuranceExtensionPatches = (
  patient: Pick<Patient, 'extension'>,
  noInsurance: boolean,
): PatchOperation[] => {
  const next = withNoInsuranceExtension({ resourceType: 'Patient', extension: patient.extension }, noInsurance);
  if (!next.extension) {
    return patient.extension ? [{ op: 'remove', path: '/extension' }] : [];
  }
  return [{ op: patient.extension ? 'replace' : 'add', path: '/extension', value: next.extension }];
};

const isActiveCoverage = (coverage: Pick<Coverage, 'status'>): boolean =>
  !coverage.status || coverage.status === 'active';

/**
 * Whether the patient has finished the insurance onboarding step: either they marked no insurance,
 * or at least one active Coverage has an insurer.
 */
export const hasAnsweredInsurance = (
  patient: Pick<Patient, 'extension'> | null | undefined,
  coverages: ReadonlyArray<Pick<Coverage, 'status' | 'payor'>> | null | undefined,
): boolean => {
  if (hasNoInsurance(patient)) return true;
  return (coverages ?? []).some(
    (coverage) => isActiveCoverage(coverage) && Boolean(insuranceFieldsFromCoverage(coverage).insurer),
  );
};
